import queryString from 'query-string';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

import useGetMyProfile from '../../Domains/User/useGetMyProfile';
import useLogout from '../../Domains/User/useLogout';
import { useConfig } from '../ConfigProvider';

import { MyProfile } from './type';
import { formatUserProfile } from './utils';

export enum LoginState {
  LOADING = 'LOADING',
  LOGGED_IN = 'LOGGED_IN',
  NOT_LOGGED_IN = 'NOT_LOGGED_IN',
}

type LoginFunctionProps = {
  anonymousUserId: string;
  redirectPath?: string;
  shouldOpenResultModal?: boolean;
};

export type Authentication = {
  userProfile: MyProfile | null;
  loginState: LoginState;
  logout: () => void;
  clearClientSession: () => void;
  registerLearnId: (loginProps: LoginFunctionProps) => void;
  loginWithFacebook: (loginProps: LoginFunctionProps) => void;
  loginWithGoogle: (loginProps: LoginFunctionProps) => void;
  loginWithLearnId: (loginProps: LoginFunctionProps) => void;
  loginWithPhoneNumber: (loginProps: LoginFunctionProps, callbackUrl: string) => void;
};

const AuthenticationContext = createContext<Authentication | undefined>(undefined);

const AuthenticationProvider = ({ children }: { children: ReactNode }) => {
  const {
    userPortal,
    facebookConfig: { facebookPortalUrl = '' },
    googleConfig: { googlePortalUrl = '' },
  } = useConfig();

  const [loginState, setLoginState] = useState(LoginState.LOADING);

  const userPortalRegisterUrl = userPortal.userPortalUrl?.register;
  const redirectRegisterUrl = userPortal.redirectUrl?.register;

  const userPortalRegisterPath = useMemo(() => {
    const searchParam = queryString.stringify({
      redirect_uri: redirectRegisterUrl,
      bu_code: userPortal?.client?.buCode,
      client_id: userPortal?.client?.id,
    });

    const registerUrl = `${userPortalRegisterUrl}/?${searchParam}`;
    return registerUrl;
  }, [
    redirectRegisterUrl,
    userPortalRegisterUrl,
    userPortal?.client?.buCode,
    userPortal?.client?.id,
  ]);

  const { data, loading, client } = useGetMyProfile({
    onError: (error) => console.error(error),
  });

  const clearClientSession = () => {
    client.cache.modify({ fields: { getMyProfile: () => null } });
  };

  const logout = () => {
    const logoutUrl = userPortal.userPortalUrl.logout;
    const callbackUrl = userPortal.callbackUrl.logout;
    const logoutRedirectUrl = userPortal.redirectUrl.logout;
    const userPortalLogoutUrl = logoutUrl
      .replace('{client_id}', userPortal?.client?.id)
      .replace('{redirect_uri}', logoutRedirectUrl)
      .replace('{callbackUrl}', callbackUrl);

    window.location.href = userPortalLogoutUrl;
  };

  const [logoutMutation] = useLogout({
    onError: (error) => console.error(error),
  });

  const handleRegisterLearnId = (loginProps: LoginFunctionProps) => {
    const { anonymousUserId, redirectPath, shouldOpenResultModal } = loginProps;
    localStorage.setItem('anonymousUserId', anonymousUserId);
    localStorage.setItem('redirectPath', redirectPath ?? '/mybooks');
    localStorage.setItem('shouldOpenResultModal', shouldOpenResultModal ? 'true' : 'false');
    const responseType = userPortal.client.responseType;
    const loginUrl = userPortal.userPortalUrl.login;
    const loginRedirectUrl = userPortal.redirectUrl.login;
    const userPortalLoginUrl = loginUrl
      .replace('{client_id}', userPortal?.client?.id)
      .replace('{redirect_uri}', loginRedirectUrl)
      .replace('{response_type}', responseType);
    // NOTE: Handle Register with Login URL
    window.location.href = userPortalLoginUrl;
  };

  const handleLoginWithLearnId = (loginProps: LoginFunctionProps) => {
    const { anonymousUserId, redirectPath, shouldOpenResultModal } = loginProps;
    localStorage.setItem('anonymousUserId', anonymousUserId);
    localStorage.setItem('redirectPath', redirectPath ?? '/mybooks');
    localStorage.setItem('shouldOpenResultModal', shouldOpenResultModal ? 'true' : 'false');
    const responseType = userPortal.client.responseType;

    const loginUrl = userPortal.userPortalUrl.login;
    const loginRedirectUrl = userPortal.redirectUrl.login;
    const userPortalLoginUrl = loginUrl
      .replace('{client_id}', userPortal?.client?.id)
      .replace('{redirect_uri}', loginRedirectUrl)
      .replace('{response_type}', responseType);
    window.location.href = userPortalLoginUrl;
  };

  const handleLoginWithFacebook = (loginProps: LoginFunctionProps) => {
    const { anonymousUserId, redirectPath, shouldOpenResultModal } = loginProps;
    localStorage.setItem('anonymousUserId', anonymousUserId);
    localStorage.setItem('redirectPath', redirectPath ?? '/mybooks');
    localStorage.setItem('shouldOpenResultModal', shouldOpenResultModal ? 'true' : 'false');
    window.location.href = facebookPortalUrl;
  };

  const handleLoginWithGoogle = (loginProps: LoginFunctionProps) => {
    const { anonymousUserId, redirectPath, shouldOpenResultModal } = loginProps;
    localStorage.setItem('anonymousUserId', anonymousUserId);
    localStorage.setItem('redirectPath', redirectPath ?? '/mybooks');
    localStorage.setItem('shouldOpenResultModal', shouldOpenResultModal ? 'true' : 'false');
    window.location.href = googlePortalUrl;
  };

  const handleLoginWithPhoneNumber = (loginProps: LoginFunctionProps, callbackUrl: string) => {
    const { anonymousUserId, redirectPath, shouldOpenResultModal } = loginProps;
    localStorage.setItem('anonymousUserId', anonymousUserId);
    localStorage.setItem('redirectPath', redirectPath ?? '/mybooks');
    localStorage.setItem('shouldOpenResultModal', shouldOpenResultModal ? 'true' : 'false');
    window.location.href = callbackUrl;
  };

  const myProfile = data?.getMyProfile;
  const userProfile = myProfile ? formatUserProfile(myProfile) : null;

  useEffect(() => {
    if (!loading) {
      if (userProfile) setLoginState(LoginState.LOGGED_IN);
      else setLoginState(LoginState.NOT_LOGGED_IN);
    }
  }, [loading, userProfile]);

  useEffect(() => {
    const queryParam = new URLSearchParams(window.location.search);
    if (queryParam.has('logout')) {
      if (queryParam.get('logout') === 'true') {
        logoutMutation();
        setLoginState(LoginState.NOT_LOGGED_IN);
      }
    }
  }, [logoutMutation]);

  return (
    <AuthenticationContext.Provider
      value={{
        clearClientSession,
        userProfile,
        loginState,
        logout,
        registerLearnId: handleRegisterLearnId,
        loginWithFacebook: handleLoginWithFacebook,
        loginWithGoogle: handleLoginWithGoogle,
        loginWithLearnId: handleLoginWithLearnId,
        loginWithPhoneNumber: handleLoginWithPhoneNumber,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

const useAuthentication = (): Authentication => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error('useAuthentication must be used within AuthenticationProvider');
  }
  return context;
};

export default AuthenticationProvider;
export { AuthenticationContext, useAuthentication };
